/**
 * NWFE-RQ-01.6 — rich-text display helpers for the question bank.
 *
 * Question fields may hold sanitized HTML (formatting toolbar) and/or the
 * generation-time auto-italic <em> wrappers for foreign terms. The bank
 * preview must RENDER that formatting (bold/italic/underline), not show tags
 * or strip them. Legacy markdown emphasis (*italic*, **bold**) is normalized
 * to tags so both styles display.
 */

import DOMPurify from 'dompurify';

const HAS_HTML = /<[a-z][^>]*>/i;

const ALLOWED_TAGS = ['em', 'i', 'strong', 'b', 'u', 's', 'br', 'p', 'ul', 'ol', 'li', 'span', 'sub', 'sup'];

/** Legacy markdown emphasis → HTML tags (*italic*, **bold**). */
export function markdownEmphasisToHtml(value: string): string {
  if (!value.includes('*')) return value;
  return value
    // Bold+italic combined: italic takes priority (client rule)
    .replace(/\*\*\*([^*\n]+?)\*\*\*/g, '<em>$1</em>')
    .replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*\w])\*(\S[^*\n]*?)\*(?![\w*])/g, '$1<em>$2</em>');
}

/** True when the value carries formatting worth rendering as HTML. */
export function hasRichText(value: any): boolean {
  return typeof value === 'string' && (HAS_HTML.test(value) || /\*\S[^*\n]*\*/.test(value));
}

/** Sanitized HTML string, safe for dangerouslySetInnerHTML. */
export function sanitizeDisplayHtml(value: string): string {
  const html = DOMPurify.sanitize(markdownEmphasisToHtml(value), { ALLOWED_TAGS, ALLOWED_ATTR: [] });
  // Italic takes priority over bold when both wrap the same term (client rule)
  return html
    .replace(/<strong>\s*(<em>[\s\S]*?<\/em>)\s*<\/strong>/gi, '$1')
    .replace(/<em>\s*<strong>([\s\S]*?)<\/strong>\s*<\/em>/gi, '<em>$1</em>')
    .replace(/<b>\s*(<i>[\s\S]*?<\/i>)\s*<\/b>/gi, '$1')
    .replace(/<i>\s*<b>([\s\S]*?)<\/b>\s*<\/i>/gi, '<i>$1</i>');
}

/** Plain-text projection for line-clamped snippets / search previews. */
export function richTextToPlain(value: any): string {
  if (typeof value !== 'string') return value == null ? '' : String(value);
  let text = value;
  if (HAS_HTML.test(text)) {
    text = text
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<li[^>]*>/gi, '- ')
      .replace(/<\/(p|li|div|ul|ol)>/gi, ' ')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>');
  }
  return text.replace(/\*\*?([^*\n]+?)\*\*?/g, '$1').replace(/\s{2,}/g, ' ').trim();
}

/** ensureEndPeriod that never doubles a period after a closing tag. */
export function ensureEndPeriodRich(value: string): string {
  if (!value || typeof value !== 'string') return value;
  const trimmed = value.trim();
  const plain = richTextToPlain(trimmed);
  return /[.!?。！？]$/.test(plain) ? trimmed : `${trimmed}.`;
}

/**
 * Renders a question text field: HTML/markdown formatting is sanitized and
 * rendered; plain values render as-is (preserving line breaks via CSS from
 * the parent's whitespace-pre-wrap class).
 */
export function RichText({ value, className }: { value: any; className?: string }) {
  if (!hasRichText(value)) {
    return <span className={className}>{value == null ? '' : String(value)}</span>;
  }
  return (
    <span
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizeDisplayHtml(String(value)) }}
    />
  );
}
